import React, { useState, useEffect } from "react";
import { SelectProduct } from "../../../types/salon.types";
import OrderItem from "./OrderItem";
import CommonButtons from "src/shared/components/CommonButtons";
import { ToastContainer } from "react-toastify";

interface OrderSectionProps {
  selectedProducts: SelectProduct[];
  discount: string;
  onQuantityChange: (productId: string, newQuantity: number) => void;
  onPriceChange: (productId: string, newPrice: string) => void;
  onRemoveProduct: (productId: string) => void;
  onDiscountChange: (discount: string) => void;
  onConfirmOrder: () => void;
  onCancelOrder: () => void;
}

const OrderSection: React.FC<OrderSectionProps> = ({
  selectedProducts,
  discount,
  onQuantityChange,
  onPriceChange,
  onRemoveProduct,
  onDiscountChange,
  onConfirmOrder,
  onCancelOrder,
}) => {
  const [localDiscount, setLocalDiscount] = useState(discount);

  const calculateSubtotal = () => {
  if (selectedProducts.length === 0) return 0;

  return selectedProducts.reduce((total, product) => {
    const amount = parseFloat(product.amount) || 0;
    const quantity = Number(product.quantity) || 1;

    return total + amount * quantity;
  }, 0);
};

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discountValue = parseFloat(discount) || 0;
    if (subtotal === 0) return 0;
    return Math.max(subtotal - discountValue, 0);
  };

  useEffect(() => {
    if (selectedProducts.length === 0 && localDiscount !== "0") {
      setLocalDiscount("0");
      onDiscountChange("0");
    }
  }, [selectedProducts, onDiscountChange]);

  const handleDiscountChange = (value: string) => {
    setLocalDiscount(value);
    let inputDiscount = parseFloat(value) || 0;
    if (inputDiscount < 0) inputDiscount = 0;
    const subtotal = calculateSubtotal();
    if (inputDiscount <= subtotal) {
      onDiscountChange(inputDiscount.toString());
    }
  };

  const handleDiscountFocus = () => {
    if (localDiscount === "0" || localDiscount === "0.00") {
      setLocalDiscount("");
    }
  };

  const handleDiscountBlur = () => {
    if (localDiscount === "") {
      setLocalDiscount("0");
      onDiscountChange("0");
    }
  };

  return (
    <div className="flex flex-col h-[calc(108.5vh-200px)] bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-[50%]">
      {/* Header */}
      <div className="flex-shrink-0 p-2 bg-gray-50 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800 text-center">
          Order Summary
          {selectedProducts.length > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({selectedProducts.length} items)
            </span>
          )}
        </h2>
      </div>

      {/* Order Items - Scrollable */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {selectedProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <p className="text-gray-400 font-medium">No items selected</p>
            <p className="text-xs text-gray-300 mt-1">Select products to build your order</p>
          </div>
        ) : (
          selectedProducts.map((product) => (
            <OrderItem
              key={product._id.$oid}
              product={product}
              onQuantityChange={onQuantityChange}
              onPriceChange={onPriceChange}
              onRemove={onRemoveProduct}
            />
          ))
        )}
      </div>

      {/* Footer - Calculations & Buttons */}
      <div className="flex-shrink-0 p-3 border-t border-gray-200 bg-gray-50">
        <div className="space-y-2">
          {/* Subtotal */}
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-600">Subtotal</span>
            <span className="text-sm font-semibold text-gray-800">
              {calculateSubtotal().toFixed(2)} QAR
            </span>
          </div>

          {/* Discount */}
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-600">Discount</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">-</span>
              <input
                type="number"
                value={localDiscount}
                onChange={(e) => {
                  let value = e.target.value.replace(/-/g, "");
                  const inputDiscount = parseFloat(value) || 0;
                  const subtotal = calculateSubtotal();
                  if (subtotal === 0) {
                    handleDiscountChange("0");
                    return;
                  }
                  const validDiscount = Math.min(inputDiscount, subtotal);
                  handleDiscountChange(validDiscount.toString());
                }}
                onFocus={handleDiscountFocus}
                onBlur={handleDiscountBlur}
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e") e.preventDefault();
                }}
                min={0}
                max={calculateSubtotal()}
                disabled={selectedProducts.length === 0}
                className="w-24 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="0"
              />
            </div>
          </div>

          {/* Total */}
          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            <span className="text-base font-bold text-gray-800">Total</span>
            <span className="text-base font-bold text-blue-600">
              {calculateTotal().toFixed(2)} QAR
            </span>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row justify-between mt-4 text-xl gap-2">
          <CommonButtons
            className="w-24"
            label="Cancel"
            variant="outlineDanger"
            onClick={onCancelOrder}
            disabled={selectedProducts.length === 0}
          />
          <CommonButtons
            // className="w-full font-bold rounded-full"
            label="Confirm Order"
            onClick={onConfirmOrder}
            disabled={selectedProducts.length === 0}
          />
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default OrderSection;